/**
 * Upload manager — the module singleton.
 *
 * Owns one Uppy instance wired to our resumable multipart backend
 * (`/api/v1/multipart_uploads`) via `@uppy/aws-s3` multipart options, plus
 * Golden Retriever for crash/reload restore. It lives at module scope so it
 * survives every Inertia `router.visit`; the corner queue widget and any
 * placeholder-slot consumer read its state through `uploadQueueStore`.
 *
 * Uppy's `uploadId` is mapped to our server-generated `upload_session_id`:
 * every signPart/listParts/complete/abort call addresses the session by that
 * id, which is exactly how the Rails endpoints are keyed.
 */
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import GoldenRetriever from "@uppy/golden-retriever";
import { toast } from "sonner";
import { csrfFetch } from "@lib/csrfFetch";
import { useUploadQueueStore } from "~/stores/uploadQueueStore";
import { ThroughputSampler } from "./throughput";
import { capturePreviewFrame, revokePreviewUrl } from "./previewFrame";
import type {
  UploadSlot,
  UploadRecord,
  UppyMeta,
  UppyResponseBody,
  QueueFile,
} from "./types";
import { sizeLimitFor, formatFileSize } from "./types";

const MULTIPART_BASE = "/api/v1/multipart_uploads";

// How long a slot may sit in "finishing" with no progress before the watchdog
// declares it stalled. Generous: `complete` is one POST that finishes in
// seconds; two minutes only trips on the silent-failure class (e.g. a bucket
// CORS config that lets part PUTs stream to 100% but makes their ETags
// unreadable, so `complete` never fires and no error event ever arrives).
export const FINISHING_STALL_MS = 2 * 60 * 1000;
export const STALLED_MESSAGE = "Upload stalled — retry";

interface CreateMultipartResponse {
  upload_session_id: string;
  key: string;
  part_size: number;
  max_parts: number;
}

interface SignPartResponse {
  url: string;
  part_number: number;
}

interface ListPartsResponse {
  parts: Array<{ part_number: number; etag: string; size: number }>;
}

async function jsonOrThrow(res: Response, context: string): Promise<unknown> {
  if (!res.ok) {
    let detail = "";
    try {
      const body = (await res.json()) as { error?: string };
      detail = body?.error ? `: ${body.error}` : "";
    } catch {
      // non-JSON error body
    }
    throw new Error(`${context} failed (${res.status})${detail}`);
  }
  return res.json();
}

class UploadManager {
  readonly uppy: Uppy<UppyMeta, UppyResponseBody>;
  // Per-file cumulative bytes + throughput sampler, keyed by Uppy file id.
  private samplers = new Map<string, ThroughputSampler>();
  // Server session id per file, so complete/abort can address the session.
  private sessionIds = new Map<string, string>();
  // Last time each file made observable progress — feeds the finishing
  // watchdog so a silent stall can never hold a row forever.
  private lastProgressAt = new Map<string, number>();
  // The in-flight `complete` POST per file, resolving to the created Upload id
  // (or null if none was ingested). If the user cancels while this is pending,
  // we chain the server-side delete onto it — the server may have already
  // created the Upload the local teardown can't see. Cleared when complete
  // settles.
  private pendingComplete = new Map<string, Promise<number | null>>();
  // Files cancelled while their `complete` was still outstanding. When complete
  // resolves for one of these, the created upload is deleted rather than kept.
  private cancelledDuringComplete = new Set<string>();
  // Upload ids a delete has already been fired for — the single guard against
  // double-deleting (cancel-during-complete AND a later remove()/cancel()).
  private deletingUploadIds = new Set<number>();
  private tickHandle: ReturnType<typeof setInterval> | null = null;
  // Monotonic counter for synthetic ids of pick-time rejections (files too big
  // to ever reach Uppy), so each rejected row is a distinct slot.
  private static rejectionSeq = 0;

  constructor() {
    this.uppy = new Uppy<UppyMeta, UppyResponseBody>({
      autoProceed: true,
      allowMultipleUploadBatches: true,
      // Reject oversized files at pick time — before an R2 multipart is opened —
      // and surface WHY in the queue. Returning `false` tells Uppy not to add
      // the file (no upload starts); we drop a terminal error slot straight into
      // the store so the corner widget shows the reason. Per-type caps live in
      // types.ts and mirror the server's Upload model.
      onBeforeFileAdded: (file) => {
        const contentType = file.type || "application/octet-stream";
        const { bytes, label } = sizeLimitFor(contentType);
        const size = file.size ?? 0;
        if (size > bytes) {
          this.surfaceRejection(file.name ?? "upload", size, bytes, label);
          return false;
        }
        return true;
      },
    });

    this.uppy.use(AwsS3, {
      // 200MB parts, matching UploadSession::PART_SIZE. A dropped connection
      // loses at most the in-flight parts. `true` (not a predicate) selects the
      // always-multipart branch, which needs no non-multipart getUploadParameters.
      shouldUseMultipart: true,
      getChunkSize: () => 200 * 1024 * 1024,
      // ~4 concurrent parts, per the plan.
      limit: 4,
      retryDelays: [0, 1000, 3000, 5000],

      createMultipartUpload: async (file) => {
        const res = await csrfFetch(MULTIPART_BASE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            declared_byte_size: file.size,
            content_type: file.type || "application/octet-stream",
            original_filename: file.name,
          }),
        });
        const data = (await jsonOrThrow(res, "createMultipartUpload")) as CreateMultipartResponse;
        this.sessionIds.set(file.id, data.upload_session_id);
        this.patchSlot(file.id, { uploadSessionId: data.upload_session_id, status: "uploading" });
        // Uppy's uploadId is our upload_session_id; key mirrors ours.
        return { uploadId: data.upload_session_id, key: data.key };
      },

      signPart: async (file, { uploadId, partNumber, body, signal }) => {
        const isFinal = this.isFinalPart(file, partNumber);
        const res = await csrfFetch(`${MULTIPART_BASE}/${uploadId}/parts/sign`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            part_number: partNumber,
            part_size: body?.size,
            is_final: isFinal,
          }),
          signal,
        });
        const data = (await jsonOrThrow(res, "signPart")) as SignPartResponse;
        return { method: "PUT", url: data.url, fields: {} };
      },

      listParts: async (_file, { uploadId, signal }) => {
        const res = await csrfFetch(`${MULTIPART_BASE}/${uploadId}/parts`, {
          method: "GET",
          signal,
        });
        const data = (await jsonOrThrow(res, "listParts")) as ListPartsResponse;
        return data.parts.map((p) => ({
          PartNumber: p.part_number,
          ETag: p.etag,
          Size: p.size,
        }));
      },

      completeMultipartUpload: async (file, { uploadId, parts, signal }) => {
        // Every byte has streamed; the row must never read "Uploading — 100%"
        // while the completion round-trip is outstanding.
        this.lastProgressAt.set(file.id, Date.now());
        this.patchSlot(file.id, {
          status: "finishing",
          progress: 100,
          bytesPerSecond: null,
          etaSeconds: null,
        });
        // Register the in-flight complete so a cancel during this window can
        // reach the Upload the server creates even though local teardown runs
        // before the POST resolves. The tracked promise never rejects (the id
        // arm defaults to null) so cancel's chain is safe to await.
        let resolveCreatedId!: (id: number | null) => void;
        this.pendingComplete.set(
          file.id,
          new Promise<number | null>((resolve) => {
            resolveCreatedId = resolve;
          })
        );
        try {
          const res = await csrfFetch(`${MULTIPART_BASE}/${uploadId}/complete`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              parts: parts.map((p) => ({ part_number: p.PartNumber, etag: p.ETag })),
            }),
            signal,
          });
          // 201 = fresh completion, 409 = replay — both return the Upload row.
          if (res.status === 201 || res.status === 409) {
            const record = (await res.json()) as UploadRecord;
            resolveCreatedId(typeof record.id === "number" ? record.id : null);
            // If the user cancelled while this POST was in flight, the row is a
            // freshly-created orphan: delete it instead of latching it.
            if (this.cancelledDuringComplete.has(file.id)) {
              if (typeof record.id === "number") void this.deleteIngested(record.id);
            } else {
              this.patchSlot(file.id, { upload: record });
            }
            return { location: undefined };
          }
          resolveCreatedId(null);
          await jsonOrThrow(res, "completeMultipartUpload");
          return { location: undefined };
        } catch (error) {
          resolveCreatedId(null);
          // A failed completion is a terminal, retryable failure — surface it
          // even if no upload-error event follows.
          this.patchSlot(file.id, {
            status: "error",
            errorMessage: error instanceof Error ? error.message : "Upload failed",
          });
          throw error;
        } finally {
          this.pendingComplete.delete(file.id);
          this.cancelledDuringComplete.delete(file.id);
        }
      },

      abortMultipartUpload: async (_file, { uploadId, signal }) => {
        if (!uploadId) return;
        await csrfFetch(`${MULTIPART_BASE}/${uploadId}/abort`, {
          method: "POST",
          signal,
        });
      },
    });

    // Golden Retriever restores the queue across a reload/crash. It cannot
    // re-hold the File blob, so restored files become ghosts the user must
    // re-select to resume — the widget documents this.
    this.uppy.use(GoldenRetriever, { serviceWorker: false });

    this.wireEvents();
    this.startTicker();
    this.wireReconnect();
  }

  // Auto-resume on reconnect. A dropped connection eventually exhausts Uppy's
  // part-level retries and lands the slot in "error"; when the browser comes
  // back online we retry those failures for the user rather than making them
  // click Retry on every row. `retryAll` only re-runs files Uppy still holds
  // with an `error` set — cancelled rows are already removed from the Uppy
  // instance (cancel() calls removeFile), so they can't be resurrected here.
  private wireReconnect(): void {
    if (typeof window === "undefined") return;
    window.addEventListener("online", this.onReconnect);
  }

  private onReconnect = (): void => {
    const { slots, order } = this.store();
    let retriedAny = false;
    for (const id of order) {
      const slot = slots[id];
      // Only failures retryAll will actually restart: an "error" slot whose
      // Uppy file still exists AND carries an `error` (retryAll's
      // #getFilesToRetry is error-gated). Flipping anything else to
      // "uploading" would be a permanent lie — the ticker's uploading branch
      // only updates throughput, so nothing would ever advance or re-fail it.
      // Two rows are deliberately skipped: a user cancellation (no Uppy file,
      // no "error" slot) and a watchdog-stalled row (Uppy file present,
      // `error: null` — Uppy never saw the silent stall). The stalled row
      // stays red for the user's manual Retry, which uses retryUpload and
      // genuinely restarts that class.
      if (!slot || slot.status !== "error") continue;
      const file = this.uppy.getFile(id);
      if (!file || !file.error) continue;
      // Reset the watchdog clock so a just-retried finishing slot doesn't
      // immediately re-trip the stall timer on the next tick.
      this.lastProgressAt.set(id, Date.now());
      this.patchSlot(id, { status: "uploading", errorMessage: null });
      retriedAny = true;
    }
    if (retriedAny) void this.uppy.retryAll().catch(() => {});
  };

  private isFinalPart(file: QueueFile, partNumber: number): boolean {
    const chunk = 200 * 1024 * 1024;
    const totalParts = Math.max(1, Math.ceil((file.size ?? 0) / chunk));
    return partNumber >= totalParts;
  }

  private sampler(id: string): ThroughputSampler {
    let s = this.samplers.get(id);
    if (!s) {
      s = new ThroughputSampler();
      this.samplers.set(id, s);
    }
    return s;
  }

  private store() {
    return useUploadQueueStore.getState();
  }

  private patchSlot(id: string, patch: Partial<UploadSlot>): void {
    this.store().patchSlot(id, patch);
  }

  private slotFromFile(file: QueueFile): UploadSlot {
    return {
      id: file.id,
      name: file.name ?? "upload",
      size: file.size ?? 0,
      status: "pending",
      progress: 0,
      bytesUploaded: 0,
      bytesPerSecond: null,
      etaSeconds: null,
      errorMessage: null,
      upload: null,
      uploadSessionId: null,
      previewUrl: null,
    };
  }

  // A file the user picked that never gets to upload — too big for its type.
  // It lands as a terminal "error" slot so the queue widget shows the reason;
  // there is no Uppy file behind it (onBeforeFileAdded returned false), so
  // retry is meaningless and X just clears the row. The id is synthetic — Uppy
  // never minted one — but unique per rejection.
  private surfaceRejection(
    name: string,
    size: number,
    limitBytes: number,
    label: string
  ): void {
    const id = `rejected-${++UploadManager.rejectionSeq}`;
    this.store().upsertSlot({
      id,
      name,
      size,
      status: "error",
      progress: 0,
      bytesUploaded: 0,
      bytesPerSecond: null,
      etaSeconds: null,
      errorMessage: `This file is ${formatFileSize(size)} — the limit for ${label} is ${formatFileSize(limitBytes)}`,
      upload: null,
      uploadSessionId: null,
      previewUrl: null,
      retryable: false,
    });
  }

  private wireEvents(): void {
    this.uppy.on("file-added", (file) => {
      this.store().upsertSlot(this.slotFromFile(file as QueueFile));
    });

    this.uppy.on("upload-progress", (file, progress) => {
      if (!file) return;
      // A straggling progress event must never downgrade a completed row.
      if (this.store().slots[file.id]?.status === "processing") return;
      const uploaded = progress.bytesUploaded ?? 0;
      const total = progress.bytesTotal ?? file.size ?? 0;
      const sampler = this.sampler(file.id);
      sampler.record(uploaded);
      this.lastProgressAt.set(file.id, Date.now());
      // 100% of bytes is not "done": the parts' ETags still have to reach the
      // server via `complete`. The row flips to "Finishing…" the instant the
      // last byte streams so it can never sit at "Uploading — 100%".
      const allBytesStreamed = total > 0 && uploaded >= total;
      this.patchSlot(file.id, {
        status: allBytesStreamed ? "finishing" : "uploading",
        bytesUploaded: uploaded,
        progress: total > 0 ? Math.min(100, Math.round((uploaded / total) * 100)) : 0,
        bytesPerSecond: allBytesStreamed ? null : sampler.bytesPerSecond(),
        etaSeconds: allBytesStreamed ? null : sampler.etaSeconds(uploaded, total),
      });
    });

    // upload-success fires after completeMultipartUpload resolves. The row is
    // already stashed on the slot; flip to the hard "processing" line.
    this.uppy.on("upload-success", (file) => {
      if (!file) return;
      this.samplers.delete(file.id);
      this.lastProgressAt.delete(file.id);
      this.patchSlot(file.id, {
        status: "processing",
        progress: 100,
        bytesPerSecond: null,
        etaSeconds: null,
      });
      // The transport is finished — the bytes are in R2 and the server owns
      // the rest. Evict the file from the Uppy instance so a completed batch
      // never lingers in `state.files`. A stale completed (or errored) file
      // poisons the NEXT batch: Uppy's `upload()` runs its retry-all branch
      // over any lingering errored file and its autoProceed bookkeeping treats
      // the instance as mid-batch, so a re-dropped file can sit at "pending"
      // forever (the "Queued" row under an "All uploads complete" header). The
      // queue-store slot is independent and survives this removal — it is the
      // source of truth the widget and any latched player read from.
      this.evictFromUppy(file.id);
    });

    // Terminal transfer failure: part-level errors after retry exhaustion, or
    // a failed `complete`. Uppy may retry internally first — the row stays
    // "Uploading"/"Finishing…" during those — but once this fires the failure
    // is contractually visible as a red row with retry.
    this.uppy.on("upload-error", (file, error) => {
      if (!file) return;
      this.samplers.delete(file.id);
      this.lastProgressAt.delete(file.id);
      this.patchSlot(file.id, {
        status: "error",
        errorMessage: error?.message ?? "Upload failed",
      });
    });

    this.uppy.on("upload-retry", (file) => {
      if (!file) return;
      this.lastProgressAt.set(file.id, Date.now());
      this.patchSlot(file.id, { status: "uploading", errorMessage: null });
    });

    // Golden Retriever fires `restored` after rehydrating queue state from a
    // reload/crash. It cannot re-hold File blobs, so unrestorable files come
    // back as ghosts — surface them as slots and flag that the user must
    // re-select the same file to resume. `restored` is a Transloadit-flavored
    // event not in the core map, hence the cast.
    (this.uppy as unknown as {
      on: (event: string, cb: () => void) => void;
    }).on("restored", () => this.reconcileRestoredFiles());
    // A restore may have already run before this handler attached.
    this.reconcileRestoredFiles();
  }

  private reconcileRestoredFiles(): void {
    const files = this.uppy.getFiles();
    let sawGhost = false;
    for (const file of files) {
      const isGhost = (file as { isGhost?: boolean }).isGhost === true;
      if (isGhost) sawGhost = true;
      const store = this.store();
      if (!store.slots[file.id]) {
        store.upsertSlot({
          ...this.slotFromFile(file as QueueFile),
          status: isGhost ? "paused" : "uploading",
          progress: file.progress?.percentage ?? 0,
          bytesUploaded: file.progress?.bytesUploaded || 0,
        });
      }
    }
    if (sawGhost) this.store().setAwaitingReselect(true);
  }

  // A slow steady ticker with two jobs. For uploading slots it keeps the ETA
  // honest even when Uppy emits no progress event for a while (a large part
  // mid-flight): throughput decays toward the real rate rather than freezing
  // on the last sample. For finishing slots it is the stall watchdog: some
  // failures (the CORS ExposeHeaders class) stream every byte and then die
  // without ever firing an error event, so time is the only signal left.
  private startTicker(): void {
    this.tickHandle = setInterval(() => {
      const { slots, order } = this.store();
      for (const id of order) {
        const slot = slots[id];
        if (!slot) continue;
        if (slot.status === "finishing") {
          const last = this.lastProgressAt.get(id);
          if (last == null) {
            this.lastProgressAt.set(id, Date.now());
          } else if (Date.now() - last >= FINISHING_STALL_MS) {
            this.lastProgressAt.delete(id);
            this.patchSlot(id, { status: "error", errorMessage: STALLED_MESSAGE });
          }
          continue;
        }
        if (slot.status !== "uploading") continue;
        const sampler = this.samplers.get(id);
        if (!sampler) continue;
        sampler.record(slot.bytesUploaded);
        this.patchSlot(id, {
          bytesPerSecond: sampler.bytesPerSecond(),
          etaSeconds: sampler.etaSeconds(slot.bytesUploaded, slot.size),
        });
      }
    }, 2000);
  }

  // ---- public control surface (used by the widget + dropzone) --------------

  // Returns the new slot's id, or null when the file was NOT enqueued. Uppy's
  // `addFile` THROWS a RestrictionError in two cases we handle here rather than
  // letting the throw abort the rest of a picked batch:
  //   - a duplicate file (`noDuplicates`) — surfaced as a polite toast.
  //   - our own oversize rejection (onBeforeFileAdded returned false) — the
  //     error slot is already in the store from surfaceRejection, so we swallow.
  // Any other throw is unexpected; re-report via a toast so it's never silent.
  addFile(file: File): string | null {
    let id: string;
    try {
      id = this.uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      // Our size rejection already put a visible error slot in the queue.
      if (message.includes("onBeforeFileAdded returned false")) return null;
      // A duplicate pick: the file is already queued. Nudge, don't fail.
      if (message.toLowerCase().includes("duplicate") || message.toLowerCase().includes("already")) {
        toast.info(`${file.name} is already in your uploads.`);
        return null;
      }
      toast.error(`Couldn't add ${file.name}. Please try again.`);
      return null;
    }
    // Kick the upload explicitly. `autoProceed` schedules its own `upload()`,
    // but its 4ms debounce and mid-batch bookkeeping make the first batch the
    // only reliably-autostarted one; a file dropped after a batch has settled
    // can otherwise stall at "pending". Calling `upload()` here starts the new
    // file immediately, and Uppy is idempotent about files already in flight
    // (`waitingFileIDs` excludes anything with `uploadStarted`). A rejected
    // promise (e.g. a restriction) is swallowed — the slot's own error path
    // surfaces it; this call must never throw into the enqueue caller.
    void this.uppy.upload().catch(() => {});
    // Tier 0: grab a local preview frame off the picked File. Best-effort and
    // fire-and-forget — it must never block enqueue. The slot exists
    // synchronously (file-added ran); patch its previewUrl when the frame lands.
    void capturePreviewFrame(file)
      .then((url) => {
        if (!url) return;
        // The slot may already be gone (cancelled before capture finished).
        if (!this.store().slots[id]) {
          revokePreviewUrl(url);
          return;
        }
        this.patchSlot(id, { previewUrl: url });
      })
      .catch(() => {
        // Capture is strictly best-effort; a failure just means no preview.
      });
    return id;
  }

  pause(id: string): void {
    this.uppy.pauseResume(id);
    this.patchSlot(id, { status: "paused" });
  }

  resume(id: string): void {
    this.uppy.pauseResume(id);
    this.patchSlot(id, { status: "uploading" });
  }

  // Retry a failed or stalled slot. Two failure classes reach here:
  //   1. Uppy saw the failure (part-level errors after retry exhaustion, or a
  //      failed `complete`): the file carries `error`, and retryUpload restarts
  //      it cleanly.
  //   2. The finishing watchdog flipped the slot to "error" on a silent stall
  //      (the CORS ExposeHeaders class): every byte streamed, `complete` never
  //      confirmed, and Uppy NEVER saw an error — the file has `error: null`
  //      and `uploadStarted` set.
  // Uppy 5's `retryUpload(id)` (Uppy.js) does NOT gate on `file.error`: it
  // clears error/isPaused, then unconditionally `#createUpload([id])` +
  // `#runUpload` with `forceAllowNewUpload: true`. So it genuinely restarts the
  // upload in BOTH cases — the multipart uploader resumes via `listParts` and
  // re-fires `completeMultipartUpload`. (The `error`-gated path is only
  // `retryAll`/`#getFilesToRetry`, which we deliberately don't use here.) If the
  // file is already gone from the Uppy instance (a rejected-at-pick-time row, or
  // one evicted after success), there is nothing to restart — clearing the row
  // is the caller's job.
  retry(id: string): void {
    if (!this.uppy.getFile(id)) return;
    // Reset the watchdog clock so a resumed finishing slot doesn't re-trip the
    // stall timer before the fresh `complete` round-trip lands.
    this.lastProgressAt.set(id, Date.now());
    this.patchSlot(id, { status: "uploading", errorMessage: null });
    void this.uppy.retryUpload(id).catch(() => {});
  }

  cancel(id: string): void {
    const ingestedId = this.ingestedUploadId(id);
    // If `complete` is still in flight, the server may create the Upload after
    // this teardown runs — flag it so completeMultipartUpload deletes the row
    // it creates instead of latching it. This closes the orphan race: cancel
    // during the completion round-trip leaves nothing behind.
    if (this.pendingComplete.has(id)) this.cancelledDuringComplete.add(id);
    // removeFile with the abort-on-remove behavior tells aws-s3 to call our
    // abortMultipartUpload, freeing the R2 multipart.
    this.uppy.removeFile(id);
    this.samplers.delete(id);
    this.sessionIds.delete(id);
    this.lastProgressAt.delete(id);
    revokePreviewUrl(this.store().slots[id]?.previewUrl ?? null);
    this.patchSlot(id, { status: "cancelled", previewUrl: null });
    // The finishing→processing race: `complete` may have already landed and
    // ingested an Upload row before the user hit X. Cancel means gone, so the
    // ingested row (and its R2 bytes) go too.
    if (ingestedId != null) void this.deleteIngested(ingestedId);
  }

  // X on a terminal row: the row leaves the queue immediately, and if the
  // transfer already ingested an Upload server-side, that upload is hard
  // deleted — R2 bytes (original, renditions, HLS tree) and DB rows. No
  // confirm dialog by design: X means delete, straight up.
  remove(id: string): void {
    const ingestedId = this.ingestedUploadId(id);
    this.uppy.removeFile(id);
    this.samplers.delete(id);
    this.sessionIds.delete(id);
    this.lastProgressAt.delete(id);
    this.store().removeSlot(id);
    if (ingestedId != null) void this.deleteIngested(ingestedId);
  }

  destroy(): void {
    if (this.tickHandle) clearInterval(this.tickHandle);
    this.tickHandle = null;
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.onReconnect);
    }
    void this.uppy.destroy();
  }

  // Drop a file from the Uppy instance without our cancel semantics (no abort,
  // no slot mutation). Used once a transfer terminates successfully so the
  // completed file cannot pollute the next batch. `removeFile` on an
  // already-gone id is a no-op in Uppy.
  private evictFromUppy(id: string): void {
    this.uppy.removeFile(id);
  }

  private ingestedUploadId(id: string): number | null {
    const uploadId = this.store().slots[id]?.upload?.id;
    return typeof uploadId === "number" ? uploadId : null;
  }

  // Fire the real server-side delete and, on success (or an already-gone 404),
  // broadcast the deletion so any latched UploadVideoSlot for this upload
  // clears. A failure (e.g. the lead-magnet guard's 422) leaves the latch
  // intact — the video still exists.
  private async deleteIngested(uploadId: number): Promise<void> {
    // One DELETE per upload id: the cancel-during-complete path and a later
    // remove()/cancel() on the same row must not both fire it.
    if (this.deletingUploadIds.has(uploadId)) return;
    this.deletingUploadIds.add(uploadId);
    try {
      const res = await csrfFetch(`/api/v1/uploads/${uploadId}`, { method: "DELETE" });
      if (res.ok || res.status === 404) {
        this.store().markUploadDeleted(uploadId);
      } else {
        // A rejected delete (e.g. the lead-magnet guard's 422) leaves the
        // upload alive — allow a later explicit remove() to retry.
        this.deletingUploadIds.delete(uploadId);
      }
    } catch {
      // Network hiccup: the queue row is already gone, and the latched player
      // (if any) keeps rendering — the safe failure mode for a delete. Clear
      // the guard so a retry can fire.
      this.deletingUploadIds.delete(uploadId);
    }
  }
}

let instance: UploadManager | null = null;

export function getUploadManager(): UploadManager {
  if (!instance) instance = new UploadManager();
  return instance;
}

export type { UploadManager };
